import { useId, useMemo, useState, type FormEvent } from 'react';
import { LiksButton } from '../../components/liks/LiksButton/LiksButton';
import { LiksDialog } from '../../components/liks/LiksDialog/LiksDialog';
import { LiksDropdown } from '../../components/liks/LiksDropdown/LiksDropdown';
import { useI18n } from '../../i18n/useI18n';
import '../knowledge/CreateKnowledgeDatabaseDialog.css';
import './CreateChatbotDialog.css';

export type CreateChatbotDialogProps = {
  title: string;
  nameLabel: string;
  namePlaceholder: string;
  knowledgeBaseLabel: string;
  knowledgeBasePlaceholder: string;
  knowledgeBaseOptions: Array<{ value: string; label: string }>;
  createLabel: string;
  cancelLabel: string;
  closeLabel: string;
  isSubmitting?: boolean;
  onCreate: (input: { name: string; knowledgeBaseId: string }) => void;
  onClose: () => void;
};

export function CreateChatbotDialog({
  title,
  nameLabel,
  namePlaceholder,
  knowledgeBaseLabel,
  knowledgeBasePlaceholder,
  knowledgeBaseOptions,
  createLabel,
  cancelLabel,
  closeLabel,
  isSubmitting = false,
  onCreate,
  onClose,
}: CreateChatbotDialogProps) {
  const { translate } = useI18n();
  const nameFieldId = useId();
  const knowledgeBaseFieldId = useId();
  const [name, setName] = useState('');
  const [knowledgeBaseId, setKnowledgeBaseId] = useState('');

  const dropdownOptions = useMemo(() => {
    if (knowledgeBaseOptions.length === 0) {
      return [{ value: '', label: knowledgeBasePlaceholder }];
    }
    return [
      { value: '', label: knowledgeBasePlaceholder },
      ...knowledgeBaseOptions,
    ];
  }, [knowledgeBaseOptions, knowledgeBasePlaceholder]);

  const selectedKnowledgeBaseId = knowledgeBaseOptions.some(
    (option) => option.value === knowledgeBaseId,
  )
    ? knowledgeBaseId
    : '';

  const isCreateDisabled =
    isSubmitting || name.trim() === '' || selectedKnowledgeBaseId === '';

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isCreateDisabled) {
      return;
    }
    onCreate({ name: name.trim(), knowledgeBaseId: selectedKnowledgeBaseId });
  };

  return (
    <LiksDialog
      isOpen
      as="form"
      size="md"
      title={title}
      onClose={onClose}
      onSubmit={handleSubmit}
      closeLabel={closeLabel}
      closeDisabled={isSubmitting}
      isDismissDisabled={isSubmitting}
      footer={
        <>
          <LiksButton
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            {cancelLabel}
          </LiksButton>
          <LiksButton type="submit" disabled={isCreateDisabled}>
            {createLabel}
          </LiksButton>
        </>
      }
    >
      <div
        className="create-chatbot-dialog__notice"
        role="note"
        aria-label={translate('chatbots.createHowItWorksLabel')}
      >
        <p className="create-chatbot-dialog__notice-title">
          {translate('chatbots.createHowItWorksTitle')}
        </p>
        <ul className="create-chatbot-dialog__notice-list">
          <li>{translate('chatbots.createHowItWorksCurrentContent')}</li>
          <li>{translate('chatbots.createHowItWorksFutureContent')}</li>
        </ul>
      </div>

      <label className="liks-knowledge-create__field" htmlFor={nameFieldId}>
        <span className="liks-knowledge-create__label">{nameLabel}</span>
        <input
          id={nameFieldId}
          className="liks-knowledge-create__input"
          value={name}
          placeholder={namePlaceholder}
          onChange={(event) => setName(event.target.value)}
          required
          autoFocus
          disabled={isSubmitting}
        />
      </label>

      <div className="liks-knowledge-create__field">
        <span
          className="liks-knowledge-create__label"
          id={knowledgeBaseFieldId}
        >
          {knowledgeBaseLabel}
        </span>
        <LiksDropdown
          value={selectedKnowledgeBaseId}
          onChange={setKnowledgeBaseId}
          options={dropdownOptions}
          aria-label={knowledgeBaseLabel}
          variant="on-light"
          disabled={isSubmitting || knowledgeBaseOptions.length === 0}
        />
      </div>
    </LiksDialog>
  );
}
